import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpParams } from "@angular/common/http";
import { Observable, of, Subject } from "rxjs";
import { Injectable, Injector } from "@angular/core";
import { PortalContextService } from "@/core/services/portal-context.service";
import { switchMap, take, filter, takeUntil } from "rxjs/operators";
import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { environment } from "src/environments/environment";
import { NavigationEnd, NavigationStart, Router } from "@angular/router";
import { Location } from "@angular/common";
import { GlobalNotificationHandler } from "@/core/utils/notifications/global-notification-handler";
import { ExtendedAuthenticationService } from '../services/extended-authentication.service';
import { AnfrageService } from "@/api/service-index";
import { AnfrageTransformerService } from "@/core/services/anfrage-transformer.service";
import { AnfrageDisplayModel } from "@/api/model-index";

@Injectable({
    providedIn: 'root'
})
export class TempAnfrageIdHttpInterceptor implements HttpInterceptor {

    private signalRConnection: HubConnection | null = null;
    private isSignalRInitialized = false;
    private replacedTempIds = new Map<string, string>(); // tempId -> realId mapping
    private navigationListenerInitialized = false;
    private cancelPendingRequests$ = new Subject<void>();
    private currentAnfrageId: string | null = null;
    private routerEventsInitialized = false;

    constructor(
        protected portalContextService: PortalContextService,
        private injector: Injector
    ) {
        this.initializeNavigationListener();
        this.initializeRouterEventsListener();
    }

    /**
     * Überwacht Router-Events und bricht SignalR-Connection sowie wartende Requests ab, wenn die Anfrage verlassen wird
     */
    private initializeRouterEventsListener(): void {
        if (this.routerEventsInitialized) {
            return;
        }

        this.routerEventsInitialized = true;
        const router = this.injector.get(Router);

        router.events.pipe(
            filter(event => event instanceof NavigationStart)
        ).subscribe((event: NavigationStart) => {
            const url = event.url;

            // Extrahiere die Anfrage-ID aus der URL (auch temp IDs)
            const anfrageIdMatch = url.match(/\/anfrage\/([^\/]+)/);
            const newAnfrageId = anfrageIdMatch ? anfrageIdMatch[1] : null;

            // Wenn wir die Anfrage verlassen oder zu einer anderen Anfrage navigieren
            if (this.currentAnfrageId && this.currentAnfrageId !== newAnfrageId) {
                this.cleanup();
            }

            this.currentAnfrageId = newAnfrageId;
        });
    }

    /**
     * Überwacht Browser-Navigation und ersetzt temp IDs automatisch
     */
    private initializeNavigationListener(): void {
        if (this.navigationListenerInitialized) {
            return;
        }

        this.navigationListenerInitialized = true;
        const location = this.injector.get(Location);

        // Nutze popstate Event um nur Browser-Back/Forward zu erkennen
        window.addEventListener('popstate', () => {
            const currentUrl = location.path();

            // Prüfe ob die URL eine temp ID enthält, die bereits ersetzt wurde
            for (const [tempId, realId] of this.replacedTempIds.entries()) {
                if (currentUrl.includes(tempId)) {
                    const newUrl = currentUrl.replace(new RegExp(tempId, 'g'), realId);

                    // Replace URL with Location.replaceState to avoid triggering a new navigation
                    location.replaceState(newUrl);
                    break;
                }
            }
        });
    }

    /**
     * Bricht alle wartenden Requests und die SignalR-Connection ab
     */
    private cleanup(): void {
        // Abbrechen aller wartenden HTTP-Requests
        this.cancelPendingRequests$.next();

        // SignalR-Connection stoppen
        if (this.signalRConnection) {
            this.signalRConnection.stop().catch(err => {
                console.error('[TempAnfrageIdInterceptor] Error stopping SignalR connection:', err);
            });
            this.signalRConnection = null;
        }

        this.isSignalRInitialized = false;
    }

    public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Hole die Werte einmal am Anfang
        const tempAnfrageId = this.portalContextService.tempAnfrageId;

        // Prüfen ob tempAnfrageId im Request vorkommt
        if (tempAnfrageId && this.containsTempAnfrageId(req, tempAnfrageId)) {
            // Initialize SignalR connection if not already done
            if (!this.isSignalRInitialized) {
                this.initializeSignalRConnection(tempAnfrageId);
            }

            // Warten bis die echte Anfrage-ID empfangen wird
            return this.portalContextService.AnfrageIdReceived.pipe(
                // Nur Events nehmen, die zu dieser tempId gehören
                filter(event => event.tempId === tempAnfrageId),
                // Nur das erste Event nehmen
                take(1),
                // Abbrechen wenn die Anfrage verlassen wird
                takeUntil(this.cancelPendingRequests$),
                // Request mit echter ID durchführen
                switchMap(event => {
                    const updatedReq = this.replaceTempAnfrageId(req, event.tempId, event.realId);
                    return next.handle(updatedReq);
                })
            );
        }

        // Falls keine temp ID vorhanden ist, prüfe ob die URL eine bereits ersetzte temp ID enthält
        if (req.url) {
            for (const [tempId, realId] of this.replacedTempIds.entries()) {
                if (req.url.includes(tempId)) {
                    const updatedReq = this.replaceTempAnfrageId(req, tempId, realId);
                    return next.handle(updatedReq);
                }
            }
        }

        return next.handle(req);
    }

    private initializeSignalRConnection(tempAnfrageId: string): void {
        if (this.isSignalRInitialized) {
            return;
        }

        this.isSignalRInitialized = true;

        const authService = this.injector.get(ExtendedAuthenticationService);

        this.signalRConnection = new HubConnectionBuilder()
            .withUrl(environment.xFoundRestUrl + "/api/anfragereceiveidhub")
            .configureLogging(LogLevel.Information)
            .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: retryContext => {
                    if (retryContext.elapsedMilliseconds < 60000) {
                        return Math.random() * 10000;
                    } else {
                        return 60000;
                    }
                }
            })
            .build();

        this.signalRConnection.on("ReceiveSaveError", (errorMessage: string) => {
            const router = this.injector.get(Router);
            router.navigate([""]).then(() => {
                const notificationHandler = this.injector.get(GlobalNotificationHandler);
                notificationHandler.showNotification(errorMessage, "error", undefined, true);
            });

            // Stop the SignalR connection as we no longer need it
            this.signalRConnection?.stop();
            this.isSignalRInitialized = false;
        });

        this.signalRConnection.on("ReceiveAnfrageId", (anfrageId: string) => {
            const oldTempId = this.portalContextService.tempAnfrageId;

            if (oldTempId) {
                // Speichere die Zuordnung für zukünftige Browser-Navigation
                this.replacedTempIds.set(oldTempId, anfrageId);

                if (this.portalContextService.CurrentAnfrage) {
                    // Set the real ID to CurrentAnfrage
                    this.portalContextService.CurrentAnfrage.id = anfrageId;
                }

                // Update the URL
                const router = this.injector.get(Router);
                const location = this.injector.get(Location);
                const newUrl = router.url.replace(oldTempId, anfrageId);

                if (router.currentNavigation()) {
                    // There's an ongoing navigation - update the browser URL immediately
                    // without triggering a new navigation that would cancel the current one
                    location.replaceState(newUrl);

                    // Wait for the current navigation to complete, then sync the router state
                    router.events.pipe(
                        filter((event: any) => event instanceof NavigationEnd),
                        take(1)
                    ).subscribe(() => {
                        const currentUrl = router.url;
                        if (currentUrl.includes(oldTempId)) {
                            // Only navigate if the URL still contains the old temp ID
                            router.navigate([currentUrl.replace(oldTempId, anfrageId)], { replaceUrl: true, state: { skipAnfrageGuard: true } });
                        }
                    });
                } else {
                    // No ongoing navigation - we can safely navigate directly
                    // Don't auto-save and keep forms dirty
                    router.navigateByUrl(newUrl, { replaceUrl: true, state: { dontAutoSave: true, stayDirty: true, skipAnfrageGuard: true, noLoadingSpinner: true, noScrollTop: true, bypassEnableDisableFormLogic: true } });
                }

                // Notify that the real ID has been received - this will unblock waiting requests
                this.portalContextService.notifyAnfrageIdReceived(oldTempId, anfrageId);

                // Stop the SignalR connection as we no longer need it
                this.signalRConnection?.stop();
                this.isSignalRInitialized = false;

                // Fetch and update the CurrentAnfrage from backend to get all real data
                const anfrageService = this.injector.get(AnfrageService);
                const anfrageTransformService = this.injector.get(AnfrageTransformerService);
                anfrageService.apiAnfrageAnfrageIdGet({ anfrageId: anfrageId }).subscribe(async response => {
                    const transformedAnfrage = await anfrageTransformService.transform(Object.assign({}, response));
                    this.patchTransformedAnfrageToCurrentAnfrage(response, transformedAnfrage);
                });
            }
        });

        this.signalRConnection.onreconnected(() => {
            this.signalRConnection?.invoke("JoinAnfrage", tempAnfrageId);
        });

        this.startConnection(tempAnfrageId, authService);
    }

    // Patcht die Felder der gefetchen und transformierten Anfrage nur, wenn sie in CurrentAnfrage noch nicht gesetzt sind, 
    // um bereits vorhandene Daten (z.B. aus Cache: FinanceData, ...) nicht zu überschreiben
    // ODER wenn sich der Wert zwischen originalAnfrage und transformedAnfrage geändert hat
    protected async patchTransformedAnfrageToCurrentAnfrage(originalAnfrage: AnfrageDisplayModel, transformedAnfrage: AnfrageDisplayModel) {
        originalAnfrage.phase = undefined;

        (Object.keys(transformedAnfrage) as Array<keyof AnfrageDisplayModel>).forEach(key => {
            const currentValue = this.portalContextService.CurrentAnfrage[key];
            const transformedValue = transformedAnfrage[key];
            const originalValue = originalAnfrage[key];

            const isCurrentNull = currentValue == null || currentValue == undefined;
            const hasTransformedValue = transformedValue != null && transformedValue != undefined;
            const hasChanged = JSON.stringify(originalValue) !== JSON.stringify(transformedValue);

            if ((isCurrentNull && hasTransformedValue) || hasChanged) {
                (this.portalContextService.CurrentAnfrage[key] as AnfrageDisplayModel[keyof AnfrageDisplayModel]) = transformedValue;
            }
        });

        this.portalContextService.CurrentAnfrage = this.portalContextService.CurrentAnfrage; // Trigger Change Detection
    }

    private async startConnection(tempAnfrageId: string, authService: ExtendedAuthenticationService): Promise<void> {
        try {
            await this.signalRConnection?.start();
            await this.signalRConnection?.invoke("JoinAnfrage", tempAnfrageId);
        } catch (err: any) {
            // Check if it's a 401 Unauthorized error
            if (err && (err.message?.includes('401') || err.message?.includes('Unauthorized') || err.statusCode === 401)) {
                try {
                    await authService.refreshAccessToken().toPromise();

                    // Retry connection with fresh token
                    await this.signalRConnection?.start();
                    await this.signalRConnection?.invoke("JoinAnfrage", tempAnfrageId);
                } catch (refreshError) {
                    console.error('[TempAnfrageIdInterceptor] Failed to refresh token or reconnect:', refreshError);
                    this.isSignalRInitialized = false;
                }
            } else {
                this.isSignalRInitialized = false;
            }
        }
    }

    /**
     * Prüft ob die tempAnfrageId im Request vorkommt (URL, Body, Query Params)
     */
    protected containsTempAnfrageId(req: HttpRequest<any>, tempAnfrageId: string): boolean {
        // Prüfe URL
        if (req.url.includes(tempAnfrageId)) {
            return true;
        }

        // Prüfe Query Params
        if (req.params.keys().length > 0) {
            for (const key of req.params.keys()) {
                const value = req.params.get(key);
                if (value && value.includes(tempAnfrageId)) {
                    return true;
                }
            }
        }

        // Prüfe Body
        if (req.body) {
            const bodyStr = JSON.stringify(req.body);
            if (bodyStr.includes(tempAnfrageId)) {
                return true;
            }
        }

        return false;
    }

    /**
     * Ersetzt die tempAnfrageId durch die neue ID im gesamten Request
     */
    protected replaceTempAnfrageId(req: HttpRequest<any>, oldId: string, newId: string): HttpRequest<any> {
        let updatedReq = req.clone();

        // Ersetze in URL
        if (req.url.includes(oldId)) {
            updatedReq = updatedReq.clone({
                url: req.url.replace(new RegExp(oldId, 'g'), newId)
            });
        }

        // Ersetze in Query Params
        if (req.params.keys().length > 0) {
            let newParams = new HttpParams();
            for (const key of req.params.keys()) {
                const values = req.params.getAll(key);
                if (values) {
                    values.forEach(value => {
                        const newValue = value ? value.replace(new RegExp(oldId, 'g'), newId) : value;
                        newParams = newParams.append(key, newValue);
                    });
                }
            }
            updatedReq = updatedReq.clone({ params: newParams });
        }

        // Ersetze in Body
        if (req.body) {
            const bodyStr = JSON.stringify(req.body);
            if (bodyStr.includes(oldId)) {
                const newBody = JSON.parse(bodyStr.replace(new RegExp(oldId, 'g'), newId));
                updatedReq = updatedReq.clone({ body: newBody });
            }
        }

        return updatedReq;
    }
}